import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Alert,
  Button,
  Flex,
  Table,
  Typography,
  Input,
  Space,
  message,
  Dropdown,
  Modal,
  Popover,
  Checkbox,
  Divider,
} from "antd";
import { useGetUsimOrderbyIccidQuery, useGetUsimOrdersByStatusMutation, useGetUsimOrdersByTypeQuery } from "../../../../api/usim/orders/UsimOrdersApi";
import OrderDetailModal from "../../shared/orderDetailModal";
import EditRetailorderPage from "../../shared/editOrderPage";
import { formatDateISO } from "../../../../utils/date";
import { ImportOutlined, FileOutlined, ExportOutlined, SettingOutlined, EditOutlined, SearchOutlined, AppstoreOutlined } from "@ant-design/icons";
import UploadJobTable from "./UploadJobTable";
import handleUploadFile from "../../../../utils/uploadFileS3";
import { BASE_URL } from "../../../../api/emptyV2Api";
import { UsimOrderItemData } from "../../../../api/usim/orders/UsimOrderItem";
import { groupOrdersByOrderNumber } from "../../shared/tableUtils";
import useDebounce from "../../../../hooks/useDebounce";
import { handleExport } from "./utilities";
import { USimStatusOptions, toUSimStatus, fromUSimStatus } from "../../shared/usimUtils";

const { Title } = Typography;

const DEFAULT_ORDERS = { items: [], count: 0, status_filters: [] };

const ENDPOINT = "usim/orders/upload?key=input"

// We now use USimStatusOptions from usimUtils

const BASE_COLUMNS = ["orderProcessedDate", "OrderNumber", "type", "sku", "iccid", "mobileNumber", "status", "activationDate", "edit", "action"];
const COLUMNS_STORAGE_KEY = "usimManageOrderTableColumns";

export default function UsimManageOrderPage() {
  const [tableData, setTableData] = useState<UsimOrderItemData[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<UsimOrderItemData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [apiSearchQuery, setApiSearchQuery] = useState("");
  const [typeFilterQuery, setTypeFilterQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>('descend');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const groupMap = new Map<string, UsimOrderItemData[]>();
  
  // Track previous filters/sorter to avoid unnecessary API calls on pagination changes
  const prevFiltersRef = useRef<Record<string, any>>({});
  const prevSorterRef = useRef<any>(null);

  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (debouncedSearch.length === 0) {
      if (apiSearchQuery !== "") {
        setApiSearchQuery("");
      }
      return;
    }

    const filtered = tableData.filter((item: UsimOrderItemData) => {
      const searchString = JSON.stringify({
        orderNumber: item.OrderNumber,
        planName: item.planName,
        type: item.type,
        platform: item.orderData?.MarketplacePlatform,
        email: item.orderData?.OrderDetails?.email,
        status: fromUSimStatus(item.status),
        iccid: item.iccid || "",
        sku: item.sku || "",
      }).toLowerCase();
      return searchString.includes(debouncedSearch.toLowerCase());
    });

    if (filtered.length === 0) {
      console.log("No local record found, calling API with debouncedSearch:", debouncedSearch);
      setApiSearchQuery(debouncedSearch);
    }
  }, [debouncedSearch]);

  const processedData = useMemo(() => {
    if (!tableData.length) {
     
      return [];
    }

    // Filter by search across fields
    const filtered = tableData.filter((item: UsimOrderItemData) => {
      if (searchQuery.length === 0) {
        return true;
      }
      const searchString = JSON.stringify({
        orderNumber: item.OrderNumber,
        planName: item.planName,
        type: item.type,
        platform: item.orderData?.MarketplacePlatform,
        email: item.orderData?.OrderDetails?.email,
        status: fromUSimStatus(item.status),
        iccid: item.iccid || "",
        sku: item.sku || "",
      }).toLowerCase();
     
      return searchString.includes(searchQuery.toLowerCase());
    });

    // Group by OrderNumber and compute latest createdAt per group

    filtered.forEach((item) => {
      const key = item.OrderNumber;
      const arr = groupMap.get(key) || [];
      arr.push(item);
      groupMap.set(key, arr);
    });

    const groups = Array.from(groupMap.entries()).map(([orderNumber, items]) => {
      const latestItem = items.reduce((acc, curr) => {
        const accDate = new Date(acc.createdAt || 0).getTime();
        const currDate = new Date(curr.createdAt || 0).getTime();
        return currDate > accDate ? curr : acc;
      }, items[0]);
      return { orderNumber, items, latestCreatedAt: latestItem.createdAt };
    });

    // Sort groups by latest createdAt according to sortOrder
    groups.sort((a, b) => {
      const aTime = new Date(a.latestCreatedAt || 0).getTime();
      const bTime = new Date(b.latestCreatedAt || 0).getTime();
      return sortOrder === 'ascend' ? aTime - bTime : bTime - aTime;
    });

    // Flatten groups while setting rowSpan for OrderNumber merging
    const flattened: UsimOrderItemData[] = [];
    groups.forEach(({ items }) => {
      const count = items.length;
      items.forEach((it, idx) => {
        flattened.push({
          ...it,
          _rowSpan: idx === 0 ? count : 0,
        });
      });
    });

    return flattened;
  }, [tableData, searchQuery, sortOrder]);

  const showDetailsModal = (order: UsimOrderItemData) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  // column sequence  - Order Date, Order Number, Product Type, SKU, ICCID/SN, Status, Activation Date
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : BASE_COLUMNS;
    } catch (e) {
      return BASE_COLUMNS;
    }
  });

  const handleColumnVisibilityChange = (checkedValues: any[]) => {
    setVisibleColumns(checkedValues as string[]);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(checkedValues));
  };

  const resetColumns = () => {
    setVisibleColumns(BASE_COLUMNS);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(BASE_COLUMNS));
  };

  const allColumns = [
    {
      title: "Order Processed Date",
      dataIndex: "orderProcessedDate",
      key: "orderProcessedDate",
      render: (text: string, record: UsimOrderItemData) => record.orderProcessedDate ? formatDateISO(record.orderProcessedDate) : "-",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => a.orderProcessedDate.localeCompare(b.orderProcessedDate),
      filteredValue: filteredInfo.orderProcessedDate || null,
    },
    {
      title: "Order Number",
      dataIndex: "OrderNumber",
      key: "OrderNumber",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      render: (text: string, record: UsimOrderItemData) => record.status?.toUpperCase() === 'CANCELLED' ? (
        <div style={{ backgroundColor: 'red', color: 'white', padding: '4px', borderRadius: '4px' }}>
          {record.OrderNumber || "-"}
        </div>
      ) : record.OrderNumber || "-",
      filteredValue: filteredInfo.OrderNumber || null,
    },

    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: Array.from(new Set(tableData.map(item => item.type)))
        .filter(Boolean)
        .map(type => ({
          text: type,
          value: type,
        })),
      onFilter: (value: any, record: UsimOrderItemData) =>
        record.type?.toUpperCase() === value.toUpperCase(),
      filteredValue: filteredInfo.type || null,
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      filteredValue: filteredInfo.sku || null,
    },
    {
      title: "ICCID/SN",
      dataIndex: "iccid",
      key: "iccid",
      filteredValue: filteredInfo.iccid || null,
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
{
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: USimStatusOptions.map(({ label, value }) => ({
        text: label,
        value: value,
      })),
      onFilter: (value: any, record: UsimOrderItemData) =>
        toUSimStatus(record.status) === value,
      render: (text: string, record: UsimOrderItemData) => fromUSimStatus(record.status),
       filteredValue: filteredInfo.status || null,
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      render: (text: string, record: UsimOrderItemData) => {
        return record.activationDate ? formatDateISO(record.activationDate) : "-";
      },
      filteredValue: filteredInfo.activationDate || null,
    },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => text ? formatDateISO(text) : "-",
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => (a.createdAt || "").localeCompare(b.createdAt || ""),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string, record: UsimOrderItemData) => (record.updatedAt || record.updated_at) ? formatDateISO(record.updatedAt || record.updated_at) : "-",
    },
    {
      title: "Shop ID",
      dataIndex: "shopId",
      key: "shopId",
    },
    {
      title: "Plan Name",
      dataIndex: "planName",
      key: "planName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Activated Date",
      dataIndex: "activatedDate",
      key: "activatedDate",
      render: (text: string) => text ? formatDateISO(text) : "-",
    },
    {
      title: "Processed By",
      dataIndex: "processedBy",
      key: "processedBy",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
    },
    {
      title: "Edit",
      key: "edit",
      render: (_: any, record: UsimOrderItemData) => (

        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEditOrder(record)
          }}
        />
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: UsimOrderItemData) => (
        <Button onClick={() => showDetailsModal(record)}>
          View Details
        </Button>
      )
    }
  ];

  const columns = allColumns.filter(col => visibleColumns.includes(col.key as string));

  const columnOptions = allColumns.map(col => ({
    label: col.title as string,
    value: col.key as string,
  }));

  const { data: apiSearchData, isError: apiError, isLoading: apiLoading, refetch: apiRefetch } = useGetUsimOrderbyIccidQuery(
    apiSearchQuery ? apiSearchQuery : "",
    {
      skip: !apiSearchQuery
    }
  );

  const { data: typeFilterData, isLoading: typeFilterLoading } = useGetUsimOrdersByTypeQuery(
    typeFilterQuery ? typeFilterQuery : "",
    {
      skip: !typeFilterQuery
    }
  );
  //  const { data: orders = { items: [] }, error, isLoading, refetch } = useGetUsimOrdersQuery();

  const [getOrdersByStatus, { data: orders = DEFAULT_ORDERS, error, isLoading }] = useGetUsimOrdersByStatusMutation();


  const loadOrders = useCallback(async (statusList: string[] = []) => {
    try {
      const result = await getOrdersByStatus({
        status_list: statusList
      }).unwrap();
      if (Array.isArray(result?.items)) {
        const groupedData = groupOrdersByOrderNumber(result.items);
        setTableData(groupedData);
      } else {
        setTableData([]);
      }
    } catch (err: any) {
      // console.error("Failed to fetch orders:", err);
      message.error("Order not found");
      setTableData([]);
    }
  }, [getOrdersByStatus]);

  useEffect(() => {
    if (apiError) {
      console.log("api error", apiError, apiSearchData)
      message.error("Order not found !");
      setTableData([]);
    }
  }, [apiError])

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (orders.items && orders.items.length) {
      console.log("orders.items", orders.items);
      const groupedData = groupOrdersByOrderNumber(orders.items);
      setTableData(groupedData);
    }
  }, [orders]);

  useEffect(() => {
    if (apiSearchData) {
      console.log("api search data", apiSearchData);
      const data: any = apiSearchData;
      // Handle cases where the API returns an array, an object with items, or a single object.
      const dataToGroup = Array.isArray(data) 
        ? data 
        : (data.items && Array.isArray(data.items) ? data.items : [data]);
      
      const groupedData = groupOrdersByOrderNumber(dataToGroup);
      console.log("grouped data", groupedData);
      setTableData(groupedData);
    }
  }, [apiSearchData, apiRefetch]);

  useEffect(() => {
    if (typeFilterData) {
      console.log("type filter data", typeFilterData);
      const data: any = typeFilterData;
      // Handle cases where the API returns an array, an object with items, or a single object.
      const dataToGroup = Array.isArray(data) 
        ? data 
        : (data.items && Array.isArray(data.items) ? data.items : [data]);
      
      const groupedData = groupOrdersByOrderNumber(dataToGroup);
      console.log("grouped data by type", groupedData);
      setTableData(groupedData);
    }
  }, [typeFilterData]);

  useEffect(() => {
    if (apiSearchQuery === "" && orders.items && orders.items.length > 0) {
      setTableData(groupOrdersByOrderNumber(orders.items));
    } else if (apiSearchQuery === "" && (!orders.items || orders.items.length === 0)) {
      setTableData([]);
    }
  }, [apiSearchQuery, orders]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUploadJobs, setShowUploadJobs] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string>("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [orderEditRecord, setEditOrderRecord] = useState<any>();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    resetUploadStates();
    setUploadFileName(file.name);
    setIsUploadModalVisible(true);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await handleUploadFile(BASE_URL + ENDPOINT, file);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsUploadComplete(true);

      message.success("Orders file uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      setIsUploadError(true);
      setUploadProgress(0);
      message.error("Failed to upload file. Please try again.");
    }
  };

  const resetUploadStates = () => {
    setUploadProgress(0);
    setIsUploadComplete(false);
    setIsUploadError(false);
    setUploadFileName("");
  };

  const handleUploadModalClose = () => {
    setIsUploadModalVisible(false);
    resetUploadStates();
    if (isUploadComplete) {
      loadOrders();
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleViewImportStatus = () => {
    setShowUploadJobs(true);
  };

  // Handle export
 

  // Handle row selection
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Options menu items
  const optionsMenuItems = [
    {
      key: "import",
      label: "Import",
      icon: <ImportOutlined />,
      onClick: handleImport,
    },
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: ()=>{
        handleExport({processedData,filteredInfo})
      },
    },
    {
      key: "viewImportStatus",
      label: "View Import Status",
      icon: <FileOutlined />,
      onClick: handleViewImportStatus,
    },
  ];


  // handle the editing part
  const handleEditOrder = (record: UsimOrderItemData) => {
    setEditOrderRecord(record);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditOrderRecord(null);
    if (apiSearchQuery) {
      apiRefetch(); // Refresh the API search results
    } else {
      loadOrders(); // Refresh the table data after editing
    }
  };

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Title level={2}>Manage Order</Title>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv,.xlsx,.xls"
        onChange={handleFileUpload}
      />

      <Flex justify="space-between" align="center">
        <Space>
          {showUploadJobs && (
            <Button icon={<FileOutlined />} onClick={() => setShowUploadJobs(false)}>
              Go back to Orders
            </Button>
          )
          }
        </Space>
        <Space>
          <Input.Search
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all fields"
            style={{ width: 300 }}
            onSearch={(value) => {
              console.log("search button clicked / enter pressed");
              setApiSearchQuery(value);
            }}
            loading={apiLoading || (searchQuery !== "" && searchQuery !== debouncedSearch)}
          />
          <Button onClick={() => {
            setSearchQuery("");
            setApiSearchQuery("");
            setFilteredInfo({});
            loadOrders();
          }} type="primary">
            Reload
          </Button>
          <Dropdown
            menu={{ items: optionsMenuItems }}
            trigger={['hover']}
          >
            <Button icon={<SettingOutlined />}>
              Options
            </Button>
          </Dropdown>
          <Popover
            content={
              <div style={{ maxWidth: 300 }}>
                <Checkbox.Group
                  options={columnOptions}
                  value={visibleColumns}
                  onChange={handleColumnVisibilityChange}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Button type="link" onClick={resetColumns} style={{ padding: 0 }}>
                  Reset to Default
                </Button>
              </div>
            }
            title="Visible Columns"
            trigger="click"
            placement="bottomRight"
          >
            <Button icon={<AppstoreOutlined />}>
              Columns
            </Button>
          </Popover>
        </Space>
      </Flex>

      {error && (
        <Alert
          type="error"
          message="Error loading orders"
          description="Failed to fetch order data. Please try again."
        />
      )}

      {showUploadJobs ? (
        <UploadJobTable />
      ) : (
        <Table
          loading={isLoading}
          dataSource={processedData}
          columns={columns}
          size="small"
          rowKey={(record: any) => `${record.OrderNumber}-${record.iccid || record.type}-${record.shopId || 1}`}
          onChange={(pagination, filters, sorter: any) => {
            const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
            setSortOrder(order ?? null);
            
            // Check if filters or sorter actually changed (not just pagination)
            const filtersChanged = JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
            const sorterChanged = JSON.stringify(sorter) !== JSON.stringify(prevSorterRef.current);
            
            if (filtersChanged || sorterChanged) {
              prevFiltersRef.current = filters;
              prevSorterRef.current = sorter;
              setFilteredInfo(filters);
              
              // Handle type filter change - call API with selected type
              const selectedTypes = filters.type 
                ? (Array.isArray(filters.type) ? filters.type[0] : filters.type)
                : "";
              
              // Handle status filter change - call API with selected statuses
              const selectedStatuses = filters.status 
                ? (Array.isArray(filters.status) ? filters.status : [filters.status]).map(s => String(s))
                : [];
              
              if (selectedTypes) {
                setTypeFilterQuery(String(selectedTypes));
              } else if (selectedStatuses.length > 0) {
                setTypeFilterQuery("");
                loadOrders(selectedStatuses);
              } else {
                setTypeFilterQuery("");
                loadOrders([]);
              }
            } else {
              // Only pagination changed, update filtered info for UI consistency
              setFilteredInfo(filters);
            }
          }}
          // rowSelection={
          //   {
          //     type: 'checkbox',
          //     selectedRowKeys,
          //     onChange: onSelectChange,
          //   }
          // }
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} orders`
          }}
        />
      )}

      {isModalVisible && (
        <OrderDetailModal
          isVisible={isModalVisible}
          onClose={handleModalClose}
          orderData={selectedOrder}
          onOrderCancelled={loadOrders}
        />
      )}

      <Modal
        title="Edit Order"
        open={showEditModal}
        onCancel={handleEditModalClose}
        footer={null}
        width={1200}
        style={{
          top: 20,
          paddingBottom: 0
        }}
        styles={{
          body: {
            height: 'calc(100vh - 120px)',
            overflow: 'auto'
          }
        }}
      >
        {orderEditRecord && (
          <EditRetailorderPage
            orderNumber={orderEditRecord.OrderNumber}
            shopId={orderEditRecord.ShopId}
          />
        )}
      </Modal>
    </Flex>
  );
}
